import { graphql } from '../gql';
import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import type {
  GetSelectedVendorListQuery,
  GetSelectedVendorListQueryVariables,
} from '../gql/graphql';

// Extract proper types from the generated schema
type ClaimNode = NonNullable<
  GetSelectedVendorListQuery['claims']['edges'][0]['node']
>;
export type VendorType = NonNullable<ClaimNode['vendors']>[0];
export type ClaimContactItem = NonNullable<ClaimNode['claimants']>[0];
export type InsuredEntityType = ClaimNode['insuredEntity'];
export type BrokerEntityType = ClaimNode['brokerEntity'];

export const GET_SELECTED_VENDOR_LIST = graphql(`
  query GetSelectedVendorList($claimFilterInput: ClaimFilterInput) {
    claims(filters: $claimFilterInput) {
      edges {
        node {
          uuid

          vendors {
            uuid
            referenceId
            claimsVendor
            hasReservePayment
            services {
              edges {
                node {
                  uuid
                  name
                }
              }
            }
            aliases {
              edges {
                node {
                  uuid
                  name
                }
              }
            }
            addresses {
              edges {
                node {
                  street1
                  street2
                  city
                  regions {
                    edges {
                      node {
                        name
                        iso2Code
                        type
                      }
                    }
                  }
                  postalCode
                  countries {
                    edges {
                      node {
                        name
                        iso2Code
                      }
                    }
                  }
                }
              }
            }
            persons {
              edges {
                node {
                  uuid
                  firstName
                  lastName
                  contacts {
                    edges {
                      node {
                        uuid
                        type
                        value
                      }
                    }
                  }
                }
              }
            }
            claimContacts {
              edges {
                node {
                  uuid
                  targetUuid
                  type
                  firstName
                  lastName
                  contacts {
                    uuid
                    type
                    value
                  }
                }
              }
            }
          }
          claimants {
            uuid
            firstName
            lastName
            type
            hasReservePayment
            companyName
            isInsured
            contacts {
              type
              value
              uuid
            }
            addresses {
              uuid
              street1
              street2
              city
              regions {
                edges {
                  node {
                    uuid
                    iso2Code
                    name
                  }
                }
              }
              postalCode
              countries {
                edges {
                  node {
                    uuid
                    iso2Code
                  }
                }
              }
            }
            claimContacts {
              uuid
              targetUuid
              type
              firstName
              lastName
              contacts {
                uuid
                type
                value
              }
            }
          }
          brokerEntity {
            cursor
            node {
              uuid
              addresses(filters: { source: PRIMARY, type: CORPORATE }) {
                edges {
                  cursor
                  node {
                    uuid
                    street1
                    street2
                    city
                    type
                    postalCode
                    source
                    regions {
                      edges {
                        cursor
                        node {
                          uuid
                          name
                          iso2Code
                        }
                      }
                    }
                    countries {
                      edges {
                        cursor
                        node {
                          uuid
                          name
                          iso2Code
                          iso3Code
                        }
                      }
                    }
                    primary
                  }
                }
              }
              aliases(filters: { groupSources: true }) {
                edges {
                  cursor
                  node {
                    uuid
                    name
                    sources
                    type
                    primary
                  }
                }
              }
              persons {
                edges {
                  cursor
                  node {
                    uuid
                    firstName
                    lastName
                    agent
                    source
                    primary
                    primaryContact
                    contacts {
                      edges {
                        cursor
                        node {
                          uuid
                          type
                          value
                          source
                          primary
                        }
                      }
                    }
                  }
                }
              }
              claimContacts {
                edges {
                  cursor
                  node {
                    uuid
                    targetUuid
                    type
                    firstName
                    lastName
                    contacts {
                      uuid
                      type
                      value
                    }
                  }
                }
                pageInfo {
                  hasNextPage
                  hasPreviousPage
                }
              }
            }
          }
          insuredEntity {
            node {
              uuid
              referenceId
              type
              submissionType
              agency
              createdAt
              updatedAt
              status
              domains(filters: { source: PRIMARY }) {
                edges {
                  node {
                    domain
                    primary
                    sources
                  }
                }
              }
              industrySectors {
                edges {
                  cursor
                  node {
                    uuid
                    name
                    naicsCode
                    sources
                    primary
                  }
                }
              }
              fundamentals {
                edges {
                  cursor
                  node {
                    uuid
                    revenue
                    projectedRevenue
                    foreignRevenue
                    projectedForeignRevenue
                    employees
                    currentRatio
                    quickRatio
                    cashRatio
                    sources
                    score
                    createdAt
                    updatedAt
                    primary
                    yearEstablished
                  }
                }
              }
              persons {
                edges {
                  cursor
                  node {
                    uuid
                    firstName
                    lastName
                    agent
                    title
                    type
                    source
                    personType {
                      uuid
                      type
                      title
                    }
                    primary
                    contacts {
                      edges {
                        cursor
                        node {
                          uuid
                          type
                          value
                          source
                          primary
                        }
                      }
                    }
                  }
                }
              }
              addresses {
                edges {
                  cursor
                  node {
                    uuid
                    street1
                    street2
                    city
                    regions {
                      edges {
                        cursor
                        node {
                          uuid
                          name
                          iso2Code
                        }
                      }
                    }
                    type
                    postalCode
                    countries {
                      edges {
                        cursor
                        node {
                          uuid
                          name
                          iso2Code
                        }
                      }
                    }
                    source
                    primary
                  }
                }
              }

              aliases {
                edges {
                  node {
                    uuid
                    name
                    type
                    sources
                    createdAt
                    updatedAt
                    primary
                  }
                }
              }
              claimContacts {
                edges {
                  cursor
                  node {
                    uuid
                    targetUuid
                    type
                    firstName
                    lastName
                    contacts {
                      uuid
                      type
                      value
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`);

export function useSelectedVendorsAndContactListQuery(claimUuid: string): {
  loading: boolean;
  data: GetSelectedVendorListQuery | undefined;
  vendors: VendorType[];
  hasVendors: boolean;
  contacts: ClaimContactItem[];
  hasContacts: boolean;
  insuredEntity: InsuredEntityType | null;
  brokerEntity: BrokerEntityType | null;
  refetch: () => void;
} {
  const { loading, data, refetch } = useQuery<
    GetSelectedVendorListQuery,
    GetSelectedVendorListQueryVariables
  >(GET_SELECTED_VENDOR_LIST, {
    variables: {
      claimFilterInput: { claimUuid },
    },
    fetchPolicy: 'no-cache',
  });

  const {
    vendors,
    hasVendors,
    contacts,
    hasContacts,
    insuredEntity,
    brokerEntity,
  } = useMemo(() => {
    // Extract all vendors and contacts from all claims
    const extractedVendors: VendorType[] = [];
    const extractedContacts: ClaimContactItem[] = [];
    let extractedInsuredEntity: InsuredEntityType | null = null;
    let extractedBrokerEntity: BrokerEntityType | null = null;

    data?.claims?.edges?.forEach((edge) => {
      const claimNode = edge.node;

      // Extract insuredEntity (only need the first one)
      if (claimNode.insuredEntity && !extractedInsuredEntity) {
        extractedInsuredEntity = claimNode.insuredEntity;
      }

      // Extract brokerEntity (only need the first one)
      if (claimNode.brokerEntity && !extractedBrokerEntity) {
        extractedBrokerEntity = claimNode.brokerEntity;
      }

      // Add ALL vendors (not just the first one)
      if (claimNode.vendors) {
        claimNode.vendors.forEach((vendor) => {
          if (vendor) {
            extractedVendors.push(vendor);
          }
        });
      }

      // Add ALL claim contacts
      if (claimNode.claimants) {
        claimNode.claimants.forEach((contact) => {
          if (contact) {
            extractedContacts.push(contact);
          }
        });
      }
    });

    return {
      vendors: extractedVendors,
      hasVendors: extractedVendors.length > 0,
      contacts: extractedContacts,
      hasContacts: extractedContacts.length > 0,
      insuredEntity: extractedInsuredEntity,
      brokerEntity: extractedBrokerEntity,
    };
  }, [data]);

  return {
    loading,
    data,
    vendors,
    hasVendors,
    contacts,
    hasContacts,
    insuredEntity,
    brokerEntity,
    refetch,
  };
}
